import {
  Box,
  Button,
  Chip,
  Stack,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  Switch,
  IconButton,
  Popover,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import CancelIcon from "@mui/icons-material/Cancel";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SettingsIcon from "@mui/icons-material/Settings";
import { useMemo, useState } from "react";
import { PAYMENT_STATUS_CONFIG } from "../../constants/paymentStatus";
import HistoryIcon from "@mui/icons-material/History";
import { useAppTranslation } from "../../hooks/useAppTranslation";

type Props = {
  status: string;
  onApprove: () => void;
  onReject: () => void;
  onBack: () => void;

  canAssignOwner?: boolean;
  ownerOptions?: { id: string; display_name?: string; email: string }[];
  selectedOwnerId?: string;
  onOwnerChange?: (userId: string | null) => void;
  onOpenAssignmentHistory?: () => void;
  ownerDisplayName?: string;
  assigningOwner?: boolean;

  canApprove?: boolean;
  isMpkIncomplete?: boolean;
  canApproveWithoutMpk?: boolean;
  isMpkBypassActive?: boolean;

  processedExternally?: boolean;
  canMarkProcessedExternally?: boolean;
  onProcessedExternallyChange?: (value: boolean) => void;
  updatingProcessedExternally?: boolean;

  paymentStatus?: string;
  paymentStatusText?: string;
  onPaymentStatusChange?: (value: string) => void;
  updatingPaymentStatus?: boolean;

  isPosted?: boolean;
};

export default function DocumentActionsBar({
  status,
  onApprove,
  onReject,
  onBack,
  canAssignOwner = false,
  ownerOptions = [],
  selectedOwnerId = "",
  onOwnerChange,
  ownerDisplayName,
  canApprove = false,
  isMpkIncomplete = false,
  canApproveWithoutMpk = false,
  isMpkBypassActive = false,
  assigningOwner = false,
  processedExternally,
  onProcessedExternallyChange,
  updatingProcessedExternally = false,
  canMarkProcessedExternally = false,
  paymentStatus,
  paymentStatusText,
  onPaymentStatusChange,
  updatingPaymentStatus = false,
  onOpenAssignmentHistory,
  isPosted = false,
}: Props) {
  const { t } = useAppTranslation("common");

  const [ownerAnchor, setOwnerAnchor] = useState<null | HTMLElement>(null);
  const [settingsAnchor, setSettingsAnchor] = useState<null | HTMLElement>(null);
  const [paymentAnchor, setPaymentAnchor] = useState<null | HTMLElement>(null);

  const isApproved = status === "Approved";
  const isRejected = status === "Rejected";

  const ownerOpen = Boolean(ownerAnchor);
  const hasDocumentSettings =
    canMarkProcessedExternally && Boolean(onProcessedExternallyChange);

  const settingsOpen = hasDocumentSettings && Boolean(settingsAnchor);

  const PAYMENT_STATUSES = useMemo(
    () =>
      Object.entries(PAYMENT_STATUS_CONFIG).map(([value, config]) => ({
        value,
        label: t(config.label),
      })),
    [t]
  );

  const normalizedPaymentStatus =
    !paymentStatus || paymentStatus.trim() === ""
      ? "__EMPTY__"
      : paymentStatus;

  const paymentConfig = PAYMENT_STATUS_CONFIG[normalizedPaymentStatus];

  const paymentLabel =
    t(paymentConfig?.label) ?? t("Do zapłaty");

  const approveTooltip = isPosted
  ? t("Dokument został już zaksięgowany i nie można zmienić jego statusu")
  : isMpkBypassActive
  ? t("MPK nie jest kompletne, ale masz uprawnienie do akceptacji dokumentu")
  : isMpkIncomplete && !canApproveWithoutMpk
  ? t("Uzupełnij MPK przed akceptacją dokumentu")
  : !canApprove
  ? t("Trwa weryfikacja MPK")
  : "";

  return (
    <Box
      sx={{
        mb: 3,
        px: 2.5,
        py: 2,
        borderRadius: 2,
        backgroundColor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      {/* TOP ROW: back + main actions */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
        spacing={1.5}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {t("Faktury")}
          </Button>

          {/* on mobile show settings icon in the same row */}
          {hasDocumentSettings && (
            <Box sx={{ display: { xs: "block", md: "none" } }}>
              <Tooltip title={t("Ustawienia dokumentu")}>
                <IconButton onClick={(e) => setSettingsAnchor(e.currentTarget)}>
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent={{ xs: "flex-end", md: "flex-end" }}
          flexWrap="wrap"
        >
          <Tooltip title={approveTooltip}>
            <span>
                {isMpkBypassActive && (
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={t("Wymiary nieuzupełnione")}
                  />
                )}
              <Button
                variant="contained"
                color="success"
                onClick={onApprove}
                disabled={!canApprove || isPosted || isApproved}
                sx={{ ml: 2, textTransform: "none" }}
              >
                {t("Akceptuj")}
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              isPosted
                ? t("Dokument został już zaksięgowany i nie można zmienić jego statusu")
                : ""
            }
          >
            <span>
              <Button
                variant="text"
                color="error"
                disabled={isRejected || isPosted || isRejected}
                startIcon={<CancelIcon />}
                onClick={onReject}
                sx={{ textTransform: "none" }}
              >
                {t("Odrzuć")}
              </Button>
            </span>
          </Tooltip>

          {/* desktop settings icon */}
          {hasDocumentSettings && (
            <Box sx={{ display: { xs: "none", md: "block" } }}>
              <Tooltip title={t("Ustawienia dokumentu")}>
                <IconButton onClick={(e) => setSettingsAnchor(e.currentTarget)}>
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Stack>
      </Stack>

      <Divider sx={{ my: 1.5 }} />

      {/* SECOND ROW: meta info (status / payment / owner) */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
        spacing={1.25}
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {/* DOCUMENT STATUS */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="caption"
              sx={{ textTransform: "uppercase", fontWeight: 700, opacity: 0.65 }}
            >
              {t("Status")}
            </Typography>

            <Chip
              size="small"
              label={t(status, {}, "statuses")}
              color={isApproved ? "success" : isRejected ? "error" : "warning"}
              variant="outlined"
            />
          </Box>

          {/* PAYMENT STATUS */}
          {onPaymentStatusChange && (
            <>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5, display: { xs: "none", md: "block" } }} />

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ textTransform: "uppercase", fontWeight: 700, opacity: 0.65 }}
                >
                  {t("Płatność")}
                </Typography>

                <Chip
                  size="small"
                  label={paymentLabel}
                  color={paymentConfig?.color ?? "default"}
                  // variant="filled"
                  onClick={(e) => setPaymentAnchor(e.currentTarget)}
                  variant="outlined"
                  clickable
                  disabled={updatingPaymentStatus}
                  icon={<KeyboardArrowDownIcon fontSize="small" />}
                  sx={{
                    cursor: updatingPaymentStatus ? "default" : "pointer",
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                    "&:focus-visible": {
                      outline: "2px solid",
                      outlineColor: "primary.main",
                      outlineOffset: 2,
                    },
                  }}
                />
              </Box>
            </>
          )}
        </Stack>

        {/* OWNER */}
        {canAssignOwner && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent={{ xs: "flex-start", md: "flex-end" }}
            flexWrap="wrap"
          >
            <Typography
              variant="caption"
              sx={{ textTransform: "uppercase", fontWeight: 700, opacity: 0.65 }}
            >
              {t("Właściciel")}
            </Typography>

            {ownerDisplayName ? (
              <Chip
                size="small"
                avatar={
                  <Avatar sx={{ width: 22, height: 22 }}>
                    {ownerDisplayName[0]?.toUpperCase()}
                  </Avatar>
                }
                label={ownerDisplayName}
                onClick={(e) => setOwnerAnchor(e.currentTarget)}
                onDelete={() => onOwnerChange?.(null)}
                deleteIcon={<CloseIcon />}
                variant="outlined"
                disabled={assigningOwner}
              />
            ) : (
              <Chip
                size="small"
                icon={<PersonAddIcon />}
                label={t("Przypisz")}
                onClick={(e) => setOwnerAnchor(e.currentTarget)}
                variant="outlined"
                disabled={assigningOwner}
              />
            )}
            {onOpenAssignmentHistory && (
              <Tooltip title={t("Historia zmian")}>
                <IconButton
                  size="small"
                  onClick={onOpenAssignmentHistory}
                >
                  <HistoryIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        )}
      </Stack>

      {/* PAYMENT MENU */}
      <Menu
        anchorEl={paymentAnchor}
        open={Boolean(paymentAnchor)}
        onClose={() => setPaymentAnchor(null)}
      >
        {/* Opcja “Brak statusu” – jeśli backend nie wspiera, to ją usuń.
            Jeśli chcesz, możemy ją zmapować na null / "" na backendzie. */}
        {/* 
        <MenuItem
          onClick={() => {
            onPaymentStatusChange?.("");
            setPaymentAnchor(null);
          }}
          disabled={updatingPaymentStatus}
        >
          Brak statusu
        </MenuItem>
        <Divider />
        */}

        {PAYMENT_STATUSES.map((s) => {
          const config = PAYMENT_STATUS_CONFIG[s.value];

          return (
            <MenuItem
              key={s.value}
              selected={normalizedPaymentStatus === s.value}
              onClick={() => {
                onPaymentStatusChange?.(s.value);
                setPaymentAnchor(null);
              }}
              disabled={updatingPaymentStatus}
            >
              <Chip
                size="small"
                label={s.label}
                color={config?.color ?? "default"}
                variant={normalizedPaymentStatus === s.value ? "filled" : "outlined"}
                sx={{ mr: 1 }}
              />

              {normalizedPaymentStatus === s.value && (
                <Typography
                  variant="caption"
                  sx={{ ml: "auto", opacity: 0.6 }}
                >
                  {t("Aktualny")}
                </Typography>
              )}
            </MenuItem>
          );
        })}
      </Menu>

      {/* OWNER MENU */}
      <Menu
        anchorEl={ownerAnchor}
        open={ownerOpen}
        onClose={() => setOwnerAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            onOwnerChange?.(null);
            setOwnerAnchor(null);
          }}
          disabled={assigningOwner}
        >
          {t("Nie przypisano")}
        </MenuItem>

        {ownerOptions.map((u) => (
          <MenuItem
            key={u.id}
            onClick={() => {
              onOwnerChange?.(u.id);
              setOwnerAnchor(null);
            }}
            disabled={assigningOwner}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Avatar sx={{ width: 28, height: 28, fontSize: 12 }}>
                {(u.display_name || u.email)[0].toUpperCase()}
              </Avatar>

              <Box>
                <Typography variant="body2">
                  {u.display_name || u.email}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {u.email}
                </Typography>
              </Box>
            </Box>
          </MenuItem>
        ))}

        {selectedOwnerId && (
          <MenuItem
            onClick={() => {
              onOwnerChange?.(null);
              setOwnerAnchor(null);
            }}
            sx={{ color: "error.main" }}
            disabled={assigningOwner}
          >
            {t("Usuń przypisanie")}
          </MenuItem>
        )}
      </Menu>

      {/* SETTINGS POPOVER */}
      {hasDocumentSettings && (
        <Popover
          open={settingsOpen}
          anchorEl={settingsAnchor}
          onClose={() => setSettingsAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Box sx={{ p: 2.5, minWidth: 260 }}>
            <Stack spacing={2}>
              <Typography variant="subtitle2" sx={{ opacity: 0.7 }}>
                {t("Ustawienia dokumentu")}
              </Typography>

              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="body2">{t("Przetworzono zewnętrznie")}</Typography>

                <Switch
                  checked={!!processedExternally}
                  onChange={(e) =>
                    onProcessedExternallyChange?.(e.target.checked)
                  }
                  disabled={updatingProcessedExternally}
                />
              </Stack>
            </Stack>
          </Box>
        </Popover>
      )}
    </Box>
  );
}